import FilterCell from "@lhd/components/FilterCell";
import { Styles } from "./styled";
import { FilterInfo } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/AppSheetComponents/FilterIcon";
import { clearSortFromOtherColumns } from "@lhd/utility";
import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import { omit } from "lodash";

interface FilterWrapperProps {
  filterInfo: FilterInfo;
  handleSetFilterInfo: React.Dispatch<React.SetStateAction<FilterInfo>>;
  displaySize?: string;
  context: any;
}

const FilterWrapper = ({
  filterInfo,
  handleSetFilterInfo,
  displaySize,
  context,
}: FilterWrapperProps) => {
  const { columnId, position } = filterInfo;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterInfo.open) return;

    const onMouseDown = (e: MouseEvent) => {
      if (!ref.current || ref.current.contains(e.target as Node)) return;
      handleSetFilterInfo((prev: any) => ({
        ...prev,
        open: false,
        columnId: null,
        position: { x: 0, y: 0 },
      }));
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [filterInfo.open, handleSetFilterInfo]);

  const options = context?.options?.[columnId ?? ""] || [];
  const hideSpecialEmptyCheckbox = !options.some(
    (opt: any) => opt?.value === "__NULL__",
  );
  const filteredOptions = options.filter(
    (opt: any) => opt?.value !== "__NULL__",
  );

  const appPart = context?.appParts?.find(
    (item: any) => String(item.id) === String(columnId),
  );
  const type = appPart?.app_part_type;

  const content = (
    <Styles
      ref={ref}
      key={columnId ?? ""}
      $displaySize={displaySize}
      className="filter-wrapper"
      style={{
        position: "fixed",
        left: `${position?.x || 0}px`,
        top: `${position?.y || 0}px`,
        zIndex: 1001,
      }}
    >
      <FilterCell
        columnKey={columnId ?? ""}
        filterOptions={filteredOptions}
        hideSpecialEmptyCheckbox={hideSpecialEmptyCheckbox}
        displaySize={displaySize}
        type={type}
        isLoading={context?.isLoadingOptions}
        onFilter={(columnKey, filterData) => {
          const newFilter = filterData.sort
            ? clearSortFromOtherColumns(filterInfo.filter || {}, columnKey)
            : { ...(filterInfo.filter || {}) };

          const isFilterCompletelyEmpty =
            filterData.isCheckAll && !filterData.sort && !filterData.search;

          const isFilterContentEmpty =
            filterData.isCheckAll && !filterData.search;

          if (isFilterCompletelyEmpty) {
            delete newFilter[columnKey];
          } else {
            newFilter[columnKey] = filterData;
          }

          // Update filterOrder: add columnKey if not already there
          const currentOrder = filterInfo.filterOrder || [];
          let newOrder = isFilterContentEmpty
            ? currentOrder.filter((id) => id !== columnKey)
            : currentOrder.includes(columnKey)
            ? currentOrder
            : [...currentOrder, columnKey];

          // Cascading clear: clear downstream filters ONLY when filter content (not sort) actually changes
          // Use !isFilterCompletelyEmpty instead of !filterData.isCheckAll so that search triggers cascading
          if (!isFilterCompletelyEmpty) {
            // Compare filter content excluding sort to detect actual filter changes
            const prevFilterContent = omit(
              filterInfo.filter?.[columnKey] || {},
              ["sort", "filterOptions"],
            );
            const newFilterContent = omit(filterData, [
              "sort",
              "filterOptions",
            ]);

            // Only clear downstream if filter content actually changed
            const filterContentChanged =
              JSON.stringify(prevFilterContent) !==
              JSON.stringify(newFilterContent);

            if (filterContentChanged) {
              // Find the index of the column being updated
              const columnIndex = newOrder.indexOf(columnKey);

              // Remove all filters that come AFTER this column
              if (columnIndex !== -1) {
                const downstreamColumns = newOrder.slice(columnIndex + 1);
                downstreamColumns.forEach((colId) => {
                  delete newFilter[colId];
                });
                // Update order to only include up to current column
                newOrder = newOrder.slice(0, columnIndex + 1);
              }
            }
          }

          handleSetFilterInfo({
            ...filterInfo,
            open: false,
            filter: newFilter,
            filterOrder: newOrder,
          });
        }}
        onCancel={() =>
          handleSetFilterInfo({
            ...filterInfo,
            open: false,
            columnId: null,
            position: { x: 0, y: 0 },
          })
        }
        initialFilter={filterInfo.filter?.[columnId ?? ""] || {}}
      />
    </Styles>
  );
  return createPortal(content, document.body);
};

export default FilterWrapper;
